import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import "./OrganisationOverview.scss";
import AnalyticsOverview from "./AnalyticsOverview";
import KeyMetrics from "../../components/layout/KeyMetrics";
import Filter from "../../components/layout/Filter";
import { getTransactionKpis } from "../../api/transactioApi/transactionsApi";
import { KpiCard } from "../../types/transactions";

function formatLocalDate(date: Date, endOfDay = false): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const time = endOfDay ? "23:59:59" : "00:00:00";
  return `${year}-${month}-${day}T${time}`;
}

const OrganisationOverview: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: firstOfMonth, endDate: now };
  });

  const [metrics, setMetrics] = useState<Array<{
    label: string;
    value: string;
    change?: string | undefined;
    since: string;
    isPositive: boolean;
    subValue?: string | undefined;
  }>>([
    { label: "Total transactions", value: "—", change: undefined, since: "", isPositive: true, subValue: undefined },
    { label: "Success rate", value: "—", change: undefined, since: "", isPositive: true, subValue: undefined },
    { label: "Total revenue", value: "—", change: undefined, since: "", isPositive: true, subValue: undefined },
    { label: "Failed transactions", value: "—", change: undefined, since: "", isPositive: false, subValue: undefined },
    { label: "Average duration", value: "—", change: undefined, since: "", isPositive: true, subValue: undefined },
  ]);

  const [kpiLoading, setKpiLoading] = useState(false);

  const loadKpis = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      setKpiLoading(true);
      const cards: KpiCard[] = await getTransactionKpis(
        formatLocalDate(startDate, false),
        formatLocalDate(endDate, true)
      );
      const mapped = cards.map((c) => ({
        label: c.label,
        value: c.value,
        change: c.change ?? undefined,
        since: c.since ?? "",
        isPositive: !!c.is_positive,
        subValue: c.sub_value ?? undefined,
      }));
      setMetrics(mapped);
    } catch (err) {
      console.error("Failed to load KPI cards:", err);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    loadKpis(dateRange.startDate, dateRange.endDate).catch(() => {});
    return () => { mounted = false; };
  }, [dateRange, loadKpis]);

  return (
    <div className="organisation-overview">
      <Sidebar className={isSidebarOpen ? "open" : ""} />
      {isSidebarOpen && <div className="overlay" onClick={closeSidebar} />}
      <div className="main-content-wrapper">
        <Header onMenuClick={toggleSidebar} />

        <main className="overview-content">
          <div className="kpi-header">
            <h2 className="kpi-heading">Overview</h2>
            <Filter
              defaultStart={dateRange.startDate}
              defaultEnd={dateRange.endDate}
              onApply={(start, end) => setDateRange({ startDate: start, endDate: end })}
            />
          </div>
          {kpiLoading ? (
            <div className="loader-container small">
              <div className="loader" />
            </div>
          ) : (
            <KeyMetrics metrics={metrics} />
          )}
          <AnalyticsOverview />
        </main>
      </div>
    </div>
  );
};

export default OrganisationOverview;
